import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import * as api from '../../util/api';

// ================ Async Thunks ================ //

/**
 * Fetch conversations for the current user
 */
const fetchConversationsPayloadCreator = async ({ userId }, { rejectWithValue }) => {
    try {
        const data = await api.getConversations(userId);
        return data.conversations || [];
    } catch (error) {
        console.error('Error fetching conversations:', error);
        return rejectWithValue(storableError(error));
    }
};

export const fetchConversationsThunk = createAsyncThunk(
    'MessagingPage/fetchConversations',
    fetchConversationsPayloadCreator
);

/**
 * Fetch messages for a specific conversation
 */
const fetchMessagesPayloadCreator = async ({ conversationId, limit = 50, skip = 0 }, { rejectWithValue }) => {
    try {
        const data = await api.getMessages(conversationId, limit, skip);
        return { conversationId, messages: data.messages || [] };
    } catch (error) {
        console.error('Error fetching messages:', error);
        return rejectWithValue(storableError(error));
    }
};

export const fetchMessagesThunk = createAsyncThunk(
    'MessagingPage/fetchMessages',
    fetchMessagesPayloadCreator
);

/**
 * Send a message via REST API (fallback if Socket.io is not connected)
 */
const sendMessagePayloadCreator = async (messageData, { rejectWithValue }) => {
    try {
        const data = await api.sendMessage(messageData);
        return data.message;
    } catch (error) {
        console.error('Error sending message:', error);
        return rejectWithValue(storableError(error));
    }
};

export const sendMessageThunk = createAsyncThunk(
    'MessagingPage/sendMessage',
    sendMessagePayloadCreator
);

/**
 * Mark messages as read
 */
const markAsReadPayloadCreator = async ({ conversationId, userId }, { rejectWithValue }) => {
    try {
        await api.markMessagesAsRead({ conversationId, userId });
        return { conversationId };
    } catch (error) {
        console.error('Error marking messages as read:', error);
        return rejectWithValue(storableError(error));
    }
};

export const markAsReadThunk = createAsyncThunk(
    'MessagingPage/markAsRead',
    markAsReadPayloadCreator
);

// ================ Slice ================ //

const messagingPageSlice = createSlice({
    name: 'MessagingPage',
    initialState: {
        conversations: [],
        conversationsError: null,
        conversationsLoading: false,

        messagesByConversation: {}, // { conversationId: [...messages] }
        messagesError: null,
        messagesLoading: false,

        selectedConversationId: null,
        sendingMessage: false,
        sendMessageError: null,
    },
    reducers: {
        // Set selected conversation
        setSelectedConversation: (state, action) => {
            state.selectedConversationId = action.payload;
        },

        // Add a new message (from Socket.io)
        addMessage: (state, action) => {
            const { conversationId, message } = action.payload;
            if (!state.messagesByConversation[conversationId]) {
                state.messagesByConversation[conversationId] = [];
            }
            
            // Deduplicate based on _id or id
            const exists = state.messagesByConversation[conversationId].some(
                m => (m._id && m._id === message._id) || (m.id && m.id === message.id)
            );

            if (!exists) {
                state.messagesByConversation[conversationId].push(message);

                // Update last message in conversation
                const conversation = state.conversations.find(c => c.conversationId === conversationId);
                if (conversation) {
                    conversation.lastMessage = {
                        message: message.message,
                        senderId: message.senderId,
                        timestamp: message.createdAt,
                    };
                    conversation.updatedAt = new Date().toISOString();
                }
            }
        },

        // Add or update a conversation
        addOrUpdateConversation: (state, action) => {
            const newConversation = action.payload;
            const existingIndex = state.conversations.findIndex(
                c => c.conversationId === newConversation.conversationId
            );

            if (existingIndex >= 0) {
                state.conversations[existingIndex] = newConversation;
            } else {
                state.conversations.unshift(newConversation);
            }
        },

        // Update message read status
        updateMessagesReadStatus: (state, action) => {
            const { conversationId } = action.payload;
            const messages = state.messagesByConversation[conversationId];
            if (messages) {
                messages.forEach(msg => {
                    msg.isRead = true;
                });
            }
        },

        // Clear all messaging data (e.g., on logout)
        clearMessagingData: (state) => {
            state.conversations = [];
            state.messagesByConversation = {};
            state.selectedConversationId = null;
            state.conversationsError = null;
            state.messagesError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchConversations
            .addCase(fetchConversationsThunk.pending, (state) => {
                state.conversationsLoading = true;
                state.conversationsError = null;
            })
            .addCase(fetchConversationsThunk.fulfilled, (state, action) => {
                state.conversationsLoading = false;
                state.conversations = action.payload;
            })
            .addCase(fetchConversationsThunk.rejected, (state, action) => {
                state.conversationsLoading = false;
                state.conversationsError = action.payload;
                console.error('Fetch conversations failed:', action.payload);
            })

            // fetchMessages
            .addCase(fetchMessagesThunk.pending, (state) => {
                state.messagesLoading = true;
                state.messagesError = null;
            })
            .addCase(fetchMessagesThunk.fulfilled, (state, action) => {
                state.messagesLoading = false;
                const { conversationId, messages } = action.payload;
                state.messagesByConversation[conversationId] = messages;
            })
            .addCase(fetchMessagesThunk.rejected, (state, action) => {
                state.messagesLoading = false;
                state.messagesError = action.payload;
                console.error('Fetch messages failed:', action.payload);
            })

            // sendMessage
            .addCase(sendMessageThunk.pending, (state) => {
                state.sendingMessage = true;
                state.sendMessageError = null;
            })
            .addCase(sendMessageThunk.fulfilled, (state, action) => {
                state.sendingMessage = false;
                
                // Add the sent message to state immediately
                const message = action.payload;
                const conversationId = message.conversationId;
                
                if (!state.messagesByConversation[conversationId]) {
                    state.messagesByConversation[conversationId] = [];
                }

                // Deduplicate logic (same as addMessage)
                const exists = state.messagesByConversation[conversationId].some(
                    m => (m._id && m._id === message._id) || (m.id && m.id === message.id)
                );

                if (!exists) {
                    state.messagesByConversation[conversationId].push(message);

                    // Update last message logic
                    const conversation = state.conversations.find(c => c.conversationId === conversationId);
                    if (conversation) {
                        conversation.lastMessage = {
                            message: message.message,
                            senderId: message.senderId,
                            timestamp: message.createdAt,
                        };
                        conversation.updatedAt = new Date().toISOString();
                    }
                }
            })
            .addCase(sendMessageThunk.rejected, (state, action) => {
                state.sendingMessage = false;
                state.sendMessageError = action.payload;
                console.error('Send message failed:', action.payload);
            })

            // markAsRead
            .addCase(markAsReadThunk.fulfilled, (state, action) => {
                const { conversationId } = action.payload;
                const messages = state.messagesByConversation[conversationId];
                if (messages) {
                    messages.forEach(msg => {
                        msg.isRead = true;
                    });
                }
            });
    },
});

export default messagingPageSlice.reducer;

export const {
    setSelectedConversation,
    addMessage,
    addOrUpdateConversation,
    updateMessagesReadStatus,
    clearMessagingData,
} = messagingPageSlice.actions;

// ================ Action Creators ================ //

// Fetch conversations
export const fetchConversations = (userId) => (dispatch) => {
    return dispatch(fetchConversationsThunk({ userId })).unwrap();
};

// Fetch messages
export const fetchMessages = (conversationId, limit, skip) => (dispatch) => {
    return dispatch(fetchMessagesThunk({ conversationId, limit, skip })).unwrap();
};

// Send message
export const sendMessage = (messageData) => (dispatch) => {
    return dispatch(sendMessageThunk(messageData)).unwrap();
};

// Mark messages as read
export const markMessagesAsRead = (conversationId, userId) => (dispatch) => {
    return dispatch(markAsReadThunk({ conversationId, userId })).unwrap();
};

// ================ Selectors ================ //

export const getConversations = (state) => state.MessagingPage.conversations;
export const getConversationsLoading = (state) => state.MessagingPage.conversationsLoading;
export const getConversationsError = (state) => state.MessagingPage.conversationsError;

export const getMessagesByConversation = (conversationId) => (state) =>
    state.MessagingPage.messagesByConversation[conversationId] || [];

export const getMessagesLoading = (state) => state.MessagingPage.messagesLoading;
export const getMessagesError = (state) => state.MessagingPage.messagesError;

export const getSelectedConversationId = (state) => state.MessagingPage.selectedConversationId;
export const getSendingMessage = (state) => state.MessagingPage.sendingMessage;

// ================ Page Load Data ================ //

export const loadData = (params, search) => (dispatch, getState) => {
    const state = getState();
    const { currentUser } = state.user;

    if (!currentUser) {
        return Promise.resolve(null);
    }

    const userId = currentUser.id.uuid;

    // Fetch conversations for the current user
    return dispatch(fetchConversations(userId));
};
